import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Check,
  ClipboardCheck,
  Fuel,
  IdCard,
  Minus,
  Plus,
  ShieldAlert,
  Smartphone,
  Wrench,
} from 'lucide-react';
import { BrandLogo } from '@/components/brand-logo';
import { ProductMockup } from './product-mockup';
import { revealClass, useReveal } from './use-reveal';
import { Button } from '@/components/ui/button';
import { PRODUCT, PRECO_POR_VEICULO_CENTS } from '@/config/product';
import { formatBRL } from '@/lib/format';
import { usePublicPlans } from '@/features/billing/api';

/**
 * Página de aquisição — rota pública, sem sessão.
 *
 * Duas decisões de conteúdo que valem registro, porque contrariam o que a
 * concorrência faz e o que estava aqui antes:
 *
 * 1. **O preço fica na cara.** O Infleet, referência do setor, esconde valor
 *    atrás de "agende uma demonstração". Isso funciona para quem tem time
 *    comercial. Sem esse time, cada lead que precisa de conversa para saber o
 *    preço é um lead perdido — então mostramos o número e uma calculadora.
 *
 * 2. **Não anunciamos o que não fazemos.** Nada de telemetria, câmera ou
 *    rastreamento em tempo real: exigem hardware no veículo e não existem
 *    aqui. Isso aparece pelo lado bom — "nada para instalar, funciona no
 *    celular" — em vez de uma lista do que falta.
 *
 * A ordem segue o AIDA: Atenção (hero pela dor), Interesse (as dores e os
 * módulos que respondem a elas), Desejo (o que muda na operação e como é
 * fácil começar) e Ação (calculadora e chamada final).
 */
export function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />
      <main>
        {/* A — Atenção */}
        <Hero />
        {/* I — Interesse */}
        <Dores />
        <Modulos />
        {/* D — Desejo */}
        <Resultados />
        <ComoFunciona />
        {/* A — Ação */}
        <Calculadora />
        <ChamadaFinal />
      </main>
      <LandingFooter />
    </div>
  );
}

function LandingHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <BrandLogo />
        <nav className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">Entrar</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/register">Criar conta</Link>
          </Button>
        </nav>
      </div>
    </header>
  );
}

// -----------------------------------------------------------------------------

function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Malha sutil ao fundo: dá profundidade sem competir com o conteúdo.
          `mask-image` evita a borda dura onde a grade termina. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.035] [background-image:linear-gradient(hsl(var(--foreground))_1px,transparent_1px),linear-gradient(90deg,hsl(var(--foreground))_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)]"
      />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 pb-20 pt-16 lg:grid-cols-[1.05fr_1fr] lg:gap-10 lg:pb-28 lg:pt-24">
        <div className="text-center lg:text-left">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1 font-mono text-xs tracking-wide text-muted-foreground">
            <span className="relative flex size-1.5">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-70 motion-reduce:hidden" />
              <span className="relative inline-flex size-1.5 rounded-full bg-success" />
            </span>
            Gestão de frota sem planilha
          </span>

          <h1 className="mt-6 text-balance text-4xl font-semibold tracking-tight sm:text-5xl lg:text-[3.25rem] lg:leading-[1.08]">
            Sua frota custa mais do que{' '}
            <span className="text-brand-accent">você consegue ver</span>.
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground lg:mx-0">
            Combustível que some, manutenção esquecida, multa que perde o prazo. O{' '}
            {PRODUCT.name} coloca checklist, abastecimento, manutenção e multas no mesmo
            lugar — o motorista registra pelo celular e você enxerga cada centavo em tempo real.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
            <Button size="lg" asChild>
              <Link to="/register">
                Criar conta
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="#preco">Quanto custa?</a>
            </Button>
          </div>

          <dl className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-4 lg:justify-start">
            {[
              ['R$ 39', 'por veículo/mês'],
              ['0', 'taxa de instalação'],
              ['∞', 'usuários e motoristas'],
            ].map(([valor, rotulo]) => (
              <div key={rotulo} className="text-center lg:text-left">
                <dt className="font-mono text-2xl font-semibold tabular-nums text-foreground">
                  {valor}
                </dt>
                <dd className="mt-0.5 text-xs text-muted-foreground">{rotulo}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:pl-4">
          <ProductMockup />
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Calculadora — a peça central da página
// -----------------------------------------------------------------------------

function Calculadora() {
  const [veiculos, setVeiculos] = useState(10);
  const { ref, visivel } = useReveal<HTMLDivElement>();

  // O preço vem da API, mesma fonte que gera a fatura, para vitrine e cobrança
  // nunca divergirem. Se a API estiver fora, cai na constante — uma landing que
  // não renderiza é pior que um número momentaneamente desatualizado.
  const { data: planos } = usePublicPlans();
  const precoCents = planos?.[0]?.pricePerVehicleCents ?? PRECO_POR_VEICULO_CENTS;

  const totalCents = veiculos * precoCents;
  const ajustar = (delta: number) =>
    setVeiculos((v) => Math.min(500, Math.max(1, v + delta)));

  return (
    <section id="preco" className="border-y border-border bg-muted/30 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-4 text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-brand-accent">
          Preço aberto, sem proposta comercial
        </p>
        <h2 className="mt-2 text-3xl font-semibold tracking-tight">
          {formatBRL(precoCents)} por veículo, por mês
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Você paga pelos veículos que estiverem ativos no mês. Tirou um da frota, a conta
          do mês seguinte já vem menor.
        </p>

        <div
          ref={ref}
          className={`mt-10 rounded-xl border border-border bg-card p-6 shadow-xl shadow-primary/5 sm:p-8 ${revealClass(visivel)}`}
        >
          <label
            htmlFor="qtd-veiculos"
            className="font-mono text-xs uppercase tracking-widest text-muted-foreground"
          >
            Quantos veículos você tem?
          </label>

          <div className="mt-4 flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              aria-label="Menos um veículo"
              onClick={() => ajustar(-1)}
              disabled={veiculos <= 1}
            >
              <Minus className="size-4" />
            </Button>

            <input
              id="qtd-veiculos"
              type="number"
              min={1}
              max={500}
              value={veiculos}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isNaN(n)) setVeiculos(Math.min(500, Math.max(1, n)));
              }}
              className="w-28 rounded-lg border border-border bg-background py-2 text-center font-mono text-3xl font-semibold tabular-nums [appearance:textfield] focus:outline-none focus:ring-2 focus:ring-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />

            <Button
              variant="outline"
              size="icon"
              aria-label="Mais um veículo"
              onClick={() => ajustar(1)}
              disabled={veiculos >= 500}
            >
              <Plus className="size-4" />
            </Button>
          </div>

          <input
            type="range"
            min={1}
            max={100}
            value={Math.min(veiculos, 100)}
            onChange={(e) => setVeiculos(Number(e.target.value))}
            aria-label="Quantidade de veículos"
            className="mt-6 w-full accent-primary"
          />

          <div className="mt-8 border-t border-border pt-6">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Sua mensalidade
            </p>
            <p className="mt-2 font-mono text-5xl font-semibold tabular-nums tracking-tight text-brand-accent transition-colors">
              {formatBRL(totalCents)}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {veiculos} {veiculos === 1 ? 'veículo' : 'veículos'} ×{' '}
              {formatBRL(precoCents)} · usuários e motoristas ilimitados
            </p>
          </div>

          <Button className="mt-8 w-full" size="lg" asChild>
            <Link to="/register">
              Criar conta
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// I — Interesse: o gestor se reconhece antes de ver o produto
// -----------------------------------------------------------------------------

const DORES = [
  {
    icon: Fuel,
    titulo: 'O combustível não fecha',
    texto: 'O cartão diz uma coisa, o hodômetro outra. Sem consumo por veículo, ninguém sabe onde o dinheiro está indo.',
  },
  {
    icon: Wrench,
    titulo: 'A manutenção só acontece quando quebra',
    texto: 'A revisão ficou para depois e o caminhão parou na estrada. Guincho, peça urgente e entrega atrasada.',
  },
  {
    icon: ShieldAlert,
    titulo: 'Multa que chega tarde demais',
    texto: 'O prazo de indicar o condutor passou e os pontos caíram na empresa. A multa ainda vem mais cara.',
  },
  {
    icon: ClipboardCheck,
    titulo: 'Checklist em foto no WhatsApp',
    texto: 'Foto perdida no grupo, papel que não volta, e na hora da auditoria ou do seguro não existe histórico.',
  },
];

function Dores() {
  return (
    <section id="dores" className="border-y border-border bg-muted/30 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-center text-3xl font-semibold tracking-tight">
          Se você gerencia frota, já passou por isso
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
          Nenhum desses problemas aparece na planilha. Todos aparecem no caixa.
        </p>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {DORES.map((dor, i) => (
            <ModuloCard key={dor.titulo} modulo={dor} indice={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// D — Desejo: o que muda depois
// -----------------------------------------------------------------------------

const RESULTADOS = [
  {
    titulo: 'Custo por veículo na palma da mão',
    texto: 'Combustível, manutenção e multas somados por placa. Você sabe qual veículo dá lucro e qual só dá despesa.',
  },
  {
    titulo: 'Preventiva no dia certo',
    texto: 'O sistema avisa pela quilometragem ou pela data. A revisão acontece antes da quebra, não depois.',
  },
  {
    titulo: 'Nenhum prazo perdido',
    texto: 'CNH, CRLV e indicação de condutor com aviso antes de vencer. Pontos vão para quem dirigia.',
  },
  {
    titulo: 'Histórico pronto para auditoria',
    texto: 'Cada checklist, abastecimento e serviço registrado, com foto e data. Seguradora e contador recebem em minutos.',
  },
];

const PUBLICO = [
  'Transportadoras e distribuidoras',
  'Construtoras e empreiteiras',
  'Prestadores de serviço com equipe na rua',
  'Frotas de 5 a 500 veículos',
];

function Resultados() {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-center text-3xl font-semibold tracking-tight">
          O que muda na sua operação
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
          Menos tempo apagando incêndio, mais controle sobre cada centavo que a frota gasta, em tempo real.
        </p>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {RESULTADOS.map((r) => (
            <div key={r.titulo} className="flex items-start gap-4 rounded-xl border border-border bg-card p-6">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
                <Check className="size-4" />
              </span>
              <div>
                <h3 className="font-medium">{r.titulo}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{r.texto}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <div className="flex items-start gap-3 rounded-xl border border-brand-accent/30 bg-brand-accent/5 p-6">
            <Smartphone className="mt-0.5 size-5 shrink-0 text-brand-accent" />
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Nada para instalar no veículo.</span>{' '}
              Funciona no celular que o motorista já tem. Sem técnico, sem equipamento, sem
              parar a frota para começar.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-muted/40 p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Feito para
            </p>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {PUBLICO.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------

const MODULOS = [
  {
    icon: ClipboardCheck,
    titulo: 'Checklist do veículo',
    texto:
      'O motorista faz a inspeção pelo celular, item por item, com observação no que estiver errado. O que reprovar vira ordem de serviço.',
  },
  {
    icon: Fuel,
    titulo: 'Abastecimento',
    texto:
      'Litros, preço e hodômetro por abastecimento, com foto do cupom. O consumo médio sai calculado, e o sistema aponta o que fugiu do padrão.',
  },
  {
    icon: Wrench,
    titulo: 'Manutenção',
    texto:
      'Planos preventivos por quilometragem ou por data. Ordens de serviço com aprovação, peças, mão de obra e histórico por veículo.',
  },
  {
    icon: ShieldAlert,
    titulo: 'Multas',
    texto:
      'Da notificação ao pagamento, com indicação de condutor dentro do prazo e controle de pontos por motorista.',
  },
  {
    icon: IdCard,
    titulo: 'Motoristas e documentos',
    texto:
      'CNH, CRLV e o resto dos documentos com data de validade. O sistema avisa antes de vencer, não depois.',
  },
  {
    icon: BarChart3,
    titulo: 'Relatórios',
    texto:
      'Custo por veículo, fechamento do mês e exportação para Excel quando o contador pedir.',
  },
];

/**
 * Cartão de módulo.
 *
 * A entrada é escalonada por índice (60ms cada) em vez de todos ao mesmo
 * tempo: o olho acompanha a sequência e a grade parece montar, não piscar.
 * Acima de ~400ms de atraso total a espera passa a incomodar, por isso o teto.
 */
function ModuloCard({
  modulo,
  indice,
}: {
  modulo: (typeof MODULOS)[number];
  indice: number;
}) {
  const { ref, visivel } = useReveal<HTMLElement>();
  const atraso = Math.min(indice * 60, 360);

  return (
    <article
      ref={ref}
      style={{ transitionDelay: visivel ? `${atraso}ms` : '0ms' }}
      className={`group rounded-xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-accent/40 hover:shadow-lg hover:shadow-primary/5 motion-reduce:hover:translate-y-0 ${revealClass(visivel)}`}
    >
      <div className="flex size-11 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-brand-accent/15 group-hover:text-brand-accent">
        <modulo.icon className="size-5" />
      </div>
      <h3 className="mt-4 font-medium">{modulo.titulo}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{modulo.texto}</p>
    </article>
  );
}

function Modulos() {
  return (
    <section id="modulos" className="py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-center text-3xl font-semibold tracking-tight">
          Um sistema para cada uma dessas dores
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
          Seis frentes, tudo incluído no mesmo preço. Não existe módulo pago à parte.
        </p>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {MODULOS.map((m, i) => (
            <ModuloCard key={m.titulo} modulo={m} indice={i} />
          ))}
        </div>
        <div className="mx-auto mt-10 flex max-w-2xl items-start gap-3 rounded-lg border border-border bg-muted/40 p-4">
          <Smartphone className="mt-0.5 size-5 shrink-0 text-primary" />
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              O motorista tem aplicativo próprio.
            </span>{' '}
            Ele abre, faz o checklist, lança o abastecimento e vê as multas indicadas a ele.
            Nada de mandar foto para o escritório digitar depois.
          </p>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------

const PASSOS = [
  {
    titulo: 'Crie a conta',
    texto: 'CNPJ, seu nome e uma senha. Leva um minuto e não pede cartão.',
  },
  {
    titulo: 'Cadastre a frota',
    texto:
      'Os veículos e os motoristas. É aqui que o preço da sua conta é definido — você vê o valor exato antes de pagar qualquer coisa.',
  },
  {
    titulo: 'Pague a primeira mensalidade',
    texto: 'Boleto, Pix ou cartão. O acesso libera assim que o pagamento é confirmado.',
  },
  {
    titulo: 'Coloque a operação para rodar',
    texto:
      'Os motoristas baixam o aplicativo e começam pelo checklist. Você acompanha tudo pelo painel.',
  },
];

function ComoFunciona() {
  return (
    <section className="border-y border-border bg-muted/30 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-4">
        <h2 className="text-center text-3xl font-semibold tracking-tight">
          Comece hoje, sem implantação
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
          Sem proposta comercial, sem reunião de diagnóstico, sem implantação paga.
        </p>

        <ol className="relative mt-12 space-y-4">
          {/* Fio vertical ligando os passos: transforma quatro cartões soltos
              numa sequência. Só a partir de sm — no celular os cartões já
              ficam colados e a linha viraria ruído. */}
          <div
            aria-hidden="true"
            className="absolute bottom-8 left-[2.4rem] top-8 hidden w-px bg-gradient-to-b from-brand-accent/40 via-border to-transparent sm:block"
          />
          {PASSOS.map((passo, i) => (
            <li
              key={passo.titulo}
              className="relative flex items-start gap-4 rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md"
            >
              <span className="relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary font-mono text-sm font-semibold tabular-nums text-primary-foreground ring-4 ring-card">
                {i + 1}
              </span>
              <div>
                <h3 className="font-medium">{passo.titulo}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{passo.texto}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------

function ChamadaFinal() {
  const { ref, visivel } = useReveal<HTMLDivElement>();

  return (
    <section className="px-4 py-16 sm:py-20">
      {/* Único bloco escuro da página. Fecha com peso e faz o CTA final
          destacar sem precisar de cor gritante. */}
      <div
        ref={ref}
        className={`relative mx-auto max-w-4xl overflow-hidden rounded-2xl bg-primary px-6 py-14 text-center text-primary-foreground sm:px-12 ${revealClass(visivel)}`}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(white_1px,transparent_1px),linear-gradient(90deg,white_1px,transparent_1px)] [background-size:36px_36px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"
        />

        <div className="relative">
          <h2 className="text-balance text-3xl font-semibold tracking-tight">
            Cada mês na planilha é dinheiro que a frota perde
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/75">
            Crie a conta agora, cadastre os veículos e veja o valor exato da mensalidade.
            Sem reunião, sem implantação, sem esperar.
          </p>
          <Button size="lg" variant="accent" className="mt-8" asChild>
            <Link to="/register">
              Quero controlar minha frota
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
          <p className="mt-4 font-mono text-xs text-primary-foreground/55">
            Sem cartão para se cadastrar · Você só paga quando decidir usar
          </p>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="border-t border-border py-10">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 sm:flex-row">
        <BrandLogo size="sm" />
        <p className="text-sm text-muted-foreground">{PRODUCT.shortDescription}</p>
        <a
          href={`mailto:${PRODUCT.supportEmail}`}
          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {PRODUCT.supportEmail}
        </a>
      </div>
    </footer>
  );
}
